import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { DataTableColumn, DataTableRow } from "../carbon/DataTable/DataTable";
import { OpportunityItem } from "../../types";
import { addressFormatter } from "../../utils";

/** The `t` returned by `useTranslation()` — typed loosely (same convention `deliveryListRows.tsx`
 * uses) since every call site here only ever calls it with a plain key. */
type TFunc = (key: string) => string;

export interface OpportunityRow extends DataTableRow {
  id: string;
  material: ReactNode;
  company: ReactNode;
  deliveryFormat: ReactNode;
  address: ReactNode;
}

/**
 * Maps `OpportunityItem[]` (a paginated page's `content`, already server-paged/server-filtered by
 * `useOpportunities` — never re-filtered client-side, same law every other migrated list in this
 * app follows) to `DataTable` rows.
 *
 * `material` stays a real `<Link to="/opportunities/material/:id">` (not a plain span) so
 * consumers — this table's own unit test, and any future e2e — can locate a row by
 * `getByRole("link", ...)`, same reasoning `deliveryListRows.tsx#buildDeliveryRows` documents for
 * `orderName`.
 */
export const buildOpportunityRows = (
  opportunities: OpportunityItem[] | undefined
): OpportunityRow[] =>
  (opportunities ?? []).map((opportunity) => ({
    id: opportunity.id,
    material: (
      <Link to={`/opportunities/material/${opportunity.id}`}>
        {opportunity.globalMaterialName}
      </Link>
    ),
    company: opportunity.company?.name ?? "-",
    deliveryFormat: opportunity.deliveryFormat,
    address: opportunity.deliveryPlace?.address
      ? addressFormatter(opportunity.deliveryPlace.address)
      : "-",
  }));

export const opportunityListHeaders = (
  t: TFunc
): DataTableColumn<OpportunityRow>[] => [
  { key: "material", header: t("opportunities.table.material") },
  { key: "company", header: t("opportunities.table.company") },
  { key: "deliveryFormat", header: t("opportunities.table.deliveryFormat") },
  { key: "address", header: t("opportunities.table.address") },
];
